import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "~/components/ui/custom-bottom-drawer";
import EnquireNowForm from "./EnquireNowForm";
import { Listing } from "./Interfaces/ListingsInterface";
import {
  getEnquireSheetStyle,
  useKeyboardInset,
} from "~/lib/hooks/useKeyboardInset";
import { cn } from "~/lib/utils";
import { getCountryName } from "~/lib/helpers/constants";
import { useEffect } from "react";
import { LEAD_FORMS, trackFormOpen } from "~/lib/ConversionTracking";

interface EnquireNowDrawerContentProps {
  isEnquireNowOpen: boolean;
  toggleEnquireModal: () => void;
  listing?: Listing;
  region?: string;
  path?: string;
}

export function EnquireNowDrawerContent({
  isEnquireNowOpen,
  toggleEnquireModal,
  listing,
  region,
  path,
}: EnquireNowDrawerContentProps) {
  const { keyboardInset, visualViewportHeight, visualViewportOffsetTop } =
    useKeyboardInset(isEnquireNowOpen);

  // Tracked here rather than on each "Enquire Now" button: six components open
  // this drawer, and any new one is covered for free.
  useEffect(() => {
    if (isEnquireNowOpen) trackFormOpen(LEAD_FORMS.ENQUIRE_NOW);
  }, [isEnquireNowOpen]);

  const layoutHeight = typeof window !== "undefined" ? window.innerHeight : 0;
  const maxSheetFraction = 0.8;
  const drawerPanelHeight =
    isEnquireNowOpen && visualViewportHeight > 0
      ? Math.min(
        Math.round(layoutHeight * maxSheetFraction),
        visualViewportHeight,
      )
      : undefined;

  const drawerPositionStyle = getEnquireSheetStyle(
    isEnquireNowOpen,
    {
      keyboardInset,
      visualViewportHeight,
      visualViewportOffsetTop,
    },
    drawerPanelHeight,
  );

  const sheetUsesFixedHeight =
    drawerPositionStyle != null &&
    typeof drawerPositionStyle === "object" &&
    "height" in drawerPositionStyle;

  return (
    <Drawer open={isEnquireNowOpen} onOpenChange={toggleEnquireModal}>
      <DrawerContent
        className='max-h-[80vh] min-h-0 overflow-hidden flex flex-col overscroll-contain'
        style={drawerPositionStyle}
      >
        <div
          className={cn(
            "flex min-h-0 min-w-0 flex-col pt-2 pb-1",
            sheetUsesFixedHeight ? "flex-1" : "max-h-full",
          )}
        >
          <div
            className={cn(
              "w-full min-h-0 overflow-y-auto overflow-x-hidden touch-pan-y",
              sheetUsesFixedHeight
                ? "flex-1 overscroll-y-auto"
                : "max-h-full self-start overscroll-contain",
            )}
            style={{
              WebkitOverflowScrolling: "touch",
              scrollPaddingTop: 4,
            }}
          >
            <DrawerHeader className='sticky top-0 z-10 border-b bg-background px-4 py-2 text-oh-black text-base font-medium'>
              <div className='flex items-center justify-between'>
                <DrawerTitle>Start Your Office Search</DrawerTitle>
              </div>
            </DrawerHeader>
            <EnquireNowForm
              canonicalCountry={listing?.country || getCountryName(region)}
              region={region}
              path={path}
              className='w-full m-0 p-0'
              lockViewportScale={isEnquireNowOpen}
            />
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
